import React, { useState } from 'react'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'

import CampaignLabel from './CampaignLabel'
import HeldChip from './HeldChip'
import BatchDetailDialog from './pipeline/BatchDetailDialog'
import { batchStateColor, totalTokens } from '../../utils/codingBatches'
import { formatImportTimestamp } from '../../utils/importBatches'
import type { CodingBatchPageResponse } from '../../types/codingBatches'

/**
 * The coding batches table, shared by the pipeline's own page and the job-wide
 * Activity view.
 *
 * ONE TABLE, TWO SCOPES, deliberately: the two views show the same rows of the
 * same table, and a reader who saw two different stories about one batch would
 * be right to distrust both. The only difference is the Pipeline column, which
 * a pipeline's own page would just repeat back at itself.
 *
 * This component owns the presentation and the paging STATE; the caller owns
 * the fetch, because the two scopes hit different endpoints. Everything that
 * decides what a row MEANS — the campaign's three states, the token rule, the
 * phaseless legacy batch — lives in utils/codingBatches.ts and CampaignLabel,
 * so neither scope can drift from the other.
 */
export interface BatchesPanelProps {
  customer: string
  job: string
  title: string
  subtitle: string
  /** Shown when the scope has no batches at all. */
  emptyText: string
  /** Name each row's pipeline — job-wide only. */
  showPipeline?: boolean
  data: CodingBatchPageResponse | undefined
  loading: boolean
  isFetching: boolean
  error: string | null
  refetch: () => void
  page: number
  rowsPerPage: number
  onPageChange: (page: number) => void
  onRowsPerPageChange: (rows: number) => void
  colorMap?: Record<string, string>
}

const BatchesPanel: React.FC<BatchesPanelProps> = ({
  customer,
  job,
  title,
  subtitle,
  emptyText,
  showPipeline = false,
  data,
  loading,
  isFetching,
  error,
  refetch,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  colorMap,
}) => {
  const [openBatchId, setOpenBatchId] = useState<string | null>(null)
  const batches = data?.batches ?? []

  return (
    <Box sx={{ overflow: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1">{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
            {data ? ` ${data.total.toLocaleString()} in total.` : ''}
          </Typography>
        </Box>
        <Tooltip title="Refresh now">
          <span>
            <IconButton color="info" onClick={refetch} disabled={isFetching} aria-label="Refresh now">
              {isFetching ? <CircularProgress size={20} color="info" /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress color="info" />
        </Box>
      )}

      {data && batches.length === 0 && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {emptyText}
          </Typography>
        </Paper>
      )}

      {batches.length > 0 && (
        <Paper elevation={1}>
          <TableContainer>
            <Table size="small" data-testid="coding-batches-table">
              <TableHead>
                <TableRow>
                  <TableCell>When (UTC)</TableCell>
                  <TableCell>State</TableCell>
                  {showPipeline && <TableCell>Pipeline</TableCell>}
                  <TableCell>Phase</TableCell>
                  <TableCell>
                    <Tooltip title="Which campaign this batch's tokens are billed to. It is the batch's own label, recorded when the batch was packed — not the campaign its records carry today.">
                      <span>Campaign</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>Model lane</TableCell>
                  <TableCell align="right">Records</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Prompt + completion tokens as the provider reported them. A batch with no usage recorded shows a dash — it is not the same as zero.">
                      <span>Tokens</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">Attempts</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.map(batch => {
                  const tokens = totalTokens(batch.token_usage)
                  return (
                    <TableRow
                      key={batch.id}
                      hover
                      onClick={() => setOpenBatchId(batch.id)}
                      data-testid="coding-batch-row"
                      sx={{
                        cursor: 'pointer',
                        ...(batch.state === 'failed'
                          ? { borderLeft: 3, borderColor: 'error.main' }
                          : {}),
                      }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">
                          {formatImportTimestamp(batch.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={batch.state}
                          color={batchStateColor(batch.state)}
                          variant={batch.state === 'pending' ? 'outlined' : 'filled'}
                        />
                        <HeldChip batch={batch} />
                      </TableCell>
                      {/* A batch outlives its pipeline — an empty name means
                          the pipeline was deleted, not that the batch is
                          unattached. */}
                      {showPipeline && (
                        <TableCell sx={{ maxWidth: 200 }}>
                          {batch.pipeline_name ? (
                            <Typography variant="body2" noWrap>
                              {batch.pipeline_name}
                            </Typography>
                          ) : (
                            <Tooltip title="This batch's pipeline has been deleted. The batch, and its cost, remain.">
                              <Typography variant="body2" color="text.disabled">
                                (deleted)
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                      {/* A legacy batch (the old in-process drain) has no phase
                          at all — say so rather than defaulting it into the
                          machine it never ran. */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {batch.phase ? (
                          <Typography variant="body2">{batch.phase}</Typography>
                        ) : (
                          <Tooltip title="Batches from before the batch controller carry no phase.">
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 160 }}>
                        <CampaignLabel labels={batch.labels} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {batch.model_version || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {batch.record_count.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={tokens === null ? 'text.disabled' : undefined}>
                          {tokens === null ? '—' : tokens.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {batch.attempt_count}/{batch.max_attempts}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography
                          variant="caption"
                          color="error.main"
                          sx={{ fontFamily: 'monospace' }}
                          noWrap
                          display="block"
                        >
                          {batch.error || ''}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={data?.total ?? 0}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_e, newPage) => onPageChange(newPage)}
            onRowsPerPageChange={e => {
              onRowsPerPageChange(parseInt(e.target.value, 10))
              onPageChange(0)
            }}
          />
        </Paper>
      )}

      <BatchDetailDialog
        customer={customer}
        job={job}
        batchId={openBatchId}
        colorMap={colorMap}
        onClose={() => setOpenBatchId(null)}
      />
    </Box>
  )
}

export default BatchesPanel
